import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { reportsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { UserPlus, Download, Loader2, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { toast } from 'sonner';

/**
 * New Admissions — how many students joined in each term, split by branch.
 *
 * A student is counted once, in the term their Date of Joining falls in
 * (student.joining_term). Students whose joining date was never recorded are
 * shown separately as "Not recorded" so they can be fixed, never guessed into
 * a term. `branch` follows the dashboard's own branch toggle.
 */
const NewAdmissionsSection = ({ branch = 'all', onOpenReport, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState('all');
  // Drill-down: { academic_year, term, branch, title }
  const [drill, setDrill] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // `silent` = background refresh after a data change: keep the current figures
  // on screen instead of flashing the spinner.
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (branch && branch !== 'all') params.branch = branch;
      if (year && year !== 'all') params.academic_year = year;
      const res = await reportsApi.newAdmissions(params);
      setData(res.data);
    } catch (err) {
      console.error(err);
      if (!silent) setData(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [branch, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // The dashboard bumps refreshKey when its data changed - reload quietly.
  useEffect(() => {
    if (refreshKey) fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Columns = branches that actually have intake, so a single-branch user
  // doesn't stare at three empty columns.
  const columns = useMemo(() => {
    if (!data) return [];
    const withData = Object.keys(data.branch_totals || {});
    const all = (data.branches || []).filter((b) => withData.includes(b));
    withData.forEach((b) => { if (!all.includes(b)) all.push(b); });
    return all.length ? all : (data.branches || []);
  }, [data]);

  const openDrill = async (term, br) => {
    const params = {
      academic_year: year === 'all' ? 'all' : year,
      term: term || 'all',
    };
    if (br) params.branch = br;
    else if (branch && branch !== 'all') params.branch = branch;

    const title = [
      term || 'All terms',
      year === 'all' ? 'all years' : year,
      br || (branch !== 'all' ? branch : 'all branches'),
    ].join(' · ');

    setDrill({ ...params, title });
    setDrillLoading(true);
    try {
      const res = await reportsApi.newAdmissionsStudents(params);
      setDrillRows(res.data.students || []);
    } catch (err) {
      toast.error('Could not load the student list');
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const openNotRecorded = async () => {
    const params = { academic_year: 'unknown' };
    if (branch && branch !== 'all') params.branch = branch;
    setDrill({ ...params, title: 'Joining date not recorded' });
    setDrillLoading(true);
    try {
      const res = await reportsApi.newAdmissionsStudents(params);
      setDrillRows(res.data.students || []);
    } catch (err) {
      toast.error('Could not load the student list');
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const handleExport = async () => {
    if (!drill) return;
    setExporting(true);
    try {
      const res = await reportsApi.exportNewAdmissionsExcel({
        academic_year: drill.academic_year,
        term: drill.term,
        branch: drill.branch,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `new_admissions_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const trend = (data?.trend || []).map((t) => ({ ...t, name: t.label }));

  return (
    <>
      <Card className="border-[#D8D9E8]" data-testid="new-admissions-section">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-lg font-semibold text-[#1C1F1D] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#3E3F96]" />
              New Admissions — Term-wise
              {branch !== 'all' && (
                <span className="text-sm font-normal text-[#5C635F]">({branch})</span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-full sm:w-44" data-testid="admissions-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All academic years</SelectItem>
                  {(data?.academic_years || []).map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="border-[#D8D9E8]"
                onClick={() => openDrill(null, null)}
                data-testid="admissions-view-all"
              >
                View list
              </Button>
              {onOpenReport && (
                <Button
                  variant="outline"
                  className="border-[#D8D9E8] text-[#3E3F96]"
                  onClick={onOpenReport}
                  data-testid="admissions-open-report"
                >
                  Open report
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="h-56 flex items-center justify-center text-[#5C635F]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading admissions…
            </div>
          ) : !data ? (
            <div className="h-24 flex items-center justify-center text-[#5C635F]">
              No admissions data available
            </div>
          ) : (
            <div className="space-y-6">
              {/* Intake trend across terms */}
              {trend.length > 0 && (
                <div className="h-56" data-testid="admissions-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trend} margin={{ top: 16, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#D8D9E8" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => [`${value} students`, 'Joined']}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #D8D9E8' }}
                      />
                      <Bar dataKey="count" fill="#3E3F96" radius={[4, 4, 0, 0]} maxBarSize={64}>
                        <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: '#5C635F' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Term x Branch matrix — click any number to see the students */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admissions-matrix">
                  <thead>
                    <tr className="border-b border-[#D8D9E8]">
                      <th className="text-left py-2 pr-4 text-xs uppercase font-semibold text-[#5C635F]">Term</th>
                      {columns.map((b) => (
                        <th key={b} className="text-right py-2 px-3 text-xs uppercase font-semibold text-[#5C635F]">{b}</th>
                      ))}
                      <th className="text-right py-2 pl-3 text-xs uppercase font-semibold text-[#3E3F96]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.matrix || []).map((row) => (
                      <tr key={row.term} className="border-b border-[#ECEDF5] hover:bg-[#F5F5FA]">
                        <td className="py-2 pr-4 font-medium text-[#1C1F1D]">{row.term}</td>
                        {columns.map((b) => {
                          const n = row.by_branch[b] || 0;
                          return (
                            <td key={b} className="text-right py-2 px-3 tabular-nums">
                              {n > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => openDrill(row.term, b)}
                                  className="text-[#3E3F96] hover:underline font-medium"
                                  data-testid={`admissions-cell-${row.term.replace(/\s+/g, '')}-${b}`}
                                >
                                  {n}
                                </button>
                              ) : (
                                <span className="text-[#B9BDBB]">0</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-right py-2 pl-3 tabular-nums font-semibold">
                          {row.total > 0 ? (
                            <button
                              type="button"
                              onClick={() => openDrill(row.term, null)}
                              className="text-[#3E3F96] hover:underline"
                            >
                              {row.total}
                            </button>
                          ) : (
                            <span className="text-[#B9BDBB]">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-[#F5F5FA]">
                      <td className="py-2 pr-4 font-semibold text-[#1C1F1D]">Total</td>
                      {columns.map((b) => (
                        <td key={b} className="text-right py-2 px-3 tabular-nums font-semibold text-[#1C1F1D]">
                          {data.branch_totals?.[b] || 0}
                        </td>
                      ))}
                      <td className="text-right py-2 pl-3 tabular-nums font-bold text-[#3E3F96]">{data.total || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {data.not_recorded?.total > 0 && (
                <div className="flex items-start gap-2 text-xs text-[#5C635F] bg-[#F5F5FA] border border-[#ECEDF5] rounded-lg p-3">
                  <Info className="w-4 h-4 text-[#D99C3B] mt-0.5 shrink-0" />
                  <span>
                    <button
                      type="button"
                      onClick={openNotRecorded}
                      className="text-[#3E3F96] hover:underline font-medium"
                      data-testid="admissions-not-recorded"
                    >
                      {data.not_recorded.total} student(s)
                    </button>
                    {' '}have no Date of Joining on record and are not counted in any term.
                    Set it on the student's Edit form to include them.
                  </span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drill-down */}
      <Dialog open={!!drill} onOpenChange={(open) => { if (!open) { setDrill(null); setDrillRows([]); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-4 pr-8">
              <span>New Admissions — {drill?.title}</span>
              <Button
                size="sm"
                variant="outline"
                className="border-[#D8D9E8]"
                onClick={handleExport}
                disabled={exporting || drillLoading || drillRows.length === 0}
                data-testid="admissions-export-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export
              </Button>
            </DialogTitle>
          </DialogHeader>

          {drillLoading ? (
            <div className="h-40 flex items-center justify-center text-[#5C635F]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : drillRows.length === 0 ? (
            <div className="h-24 flex items-center justify-center text-[#5C635F]">No students</div>
          ) : (
            <>
              <p className="text-sm text-[#5C635F]">{drillRows.length} student(s)</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase">Student ID</TableHead>
                    <TableHead className="text-xs uppercase">Name</TableHead>
                    <TableHead className="text-xs uppercase">Branch</TableHead>
                    <TableHead className="text-xs uppercase">Class</TableHead>
                    <TableHead className="text-xs uppercase">Joined</TableHead>
                    <TableHead className="text-xs uppercase">Term</TableHead>
                    <TableHead className="text-xs uppercase">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillRows.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs">{s.student_id}</TableCell>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell>{s.branch}</TableCell>
                      <TableCell>{s.current_class}</TableCell>
                      <TableCell className="tabular-nums">
                        {s.date_of_joining || '—'}
                        {s.joining_source && s.joining_source !== 'manual' && (
                          <span className="ml-1 text-[10px] text-[#8A8E8B]" title={`Derived from ${s.joining_source.replace('_', ' ')}`}>
                            (derived)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{s.joining_term ? `${s.joining_term} · ${s.joining_year}` : '—'}</TableCell>
                      <TableCell className="capitalize">{s.lifecycle || s.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewAdmissionsSection;
